
import {
  AppWindow,
  Loader2,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Link2,
  Copy,
  CopyCheck,
  Tag,
  Trash2,
  Folder,
  FilePen,
  Check,
  SquareMinus,
  Workflow,
  LockKeyhole,
  CopyMinus,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { toast } from "sonner";

import { ItemFlowRunsSheet } from "@/modules/flow/components/item-flow-runs-sheet";
import { getSourceName, ItemMoveDraggable, useDeleteItemMutation, useItemActions, useLayoutMode, useMineItemsData, useOpenItemPreview, useSelectedItemId } from "@/modules/mine";
import { BulkSelectionToolbar } from "@/modules/mine/components/bulk-selection-toolbar";
import { ItemSkeleton } from "@/modules/mine/components/item-skeleton";
import { AddLink } from "@/modules/mine/components/items/add-link";
import { AddTextOverlay } from "@/modules/mine/components/items/add-text";
import { BulkAddTags, BulkRemoveTags } from "@/modules/mine/components/items/bulk-edit-tags";
import { EditImageItem } from "@/modules/mine/components/items/edit-image-item";
import { EditItem } from "@/modules/mine/components/items/edit-item";
import { MoveItem } from "@/modules/mine/components/items/move-item";
import { ItemCard } from "@/modules/mine/components/items-display/item-card";
import { ItemHeadline } from "@/modules/mine/components/items-display/item-headline";
import { ItemRow } from "@/modules/mine/components/items-display/item-row";
import { ItemWaterfall } from "@/modules/mine/components/items-display/item-waterfall";
import { ItemsFilter } from "@/modules/mine/components/items-filter";
import { useSmartStickyToolbar } from "@/modules/mine/hooks/use-smart-sticky-toolbar";
import { useFolderPrivacyStatus } from "@/modules/mine/privacy/use-folder-privacy-status";
import type { Item } from "@/modules/mine/types";
import { useWorkspaceContext } from "@/modules/mine/workspace/use-workspace-context";
import { useDialog } from "@/shared/components/dialog";
import { useSheet } from "@/shared/components/sheet";
import { useRouteInfo } from "@/shared/hooks/use-route-info";
import { useInfiniteScroll } from "@/shared/hooks/useInfiniteScroll";
import { Button } from "@/shared/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shared/ui/context-menu"
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from "@/shared/ui/empty";
import { useSidebar } from "@/shared/ui/sidebar";
import { useFolderPrivacyDialog } from "@/widgets/privacy/use-folder-privacy-dialog";

/**
 * 根据布局模式返回对应的容器样式
 * 使用Tailwind CSS的响应式类名
 */
const getContainerClass = (mode: string): string => {
  const classMap: Record<string, string> = {
    // 卡片：宽窗口最小宽度 240px，窄窗口尺寸见 workspace-density.css
    // 使用 auto-fill 让网格自动计算每行列数，minmax 控制卡片宽度范围
    card: "grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-4 auto-rows-[320px]",
    // 列表：条目之间保留 4px 间距
    list: "space-y-1",
    // 极简：垂直堆叠，间距1
    headline: "space-y-1",
    // 瀑布流：CSS多列布局
    waterfall: "columns-[260px] gap-4",
  };
  return classMap[mode] || classMap.card;
};

const ITEM_COMPONENTS = {
  card: ItemCard,
  list: ItemRow,
  headline: ItemHeadline,
  waterfall: ItemWaterfall,
} as const;

function getActionItemTitle(item: Item) {
  const title = item.title?.trim();

  if (title) {
    return title;
  }

  if (item.format === "website" && item.url) {
    return getSourceName(item.url);
  }

  if (item.format === "image") {
    return item.url ? getSourceName(item.url) : "未命名图片";
  }

  if (item.format === "text") {
    return "未命名文本";
  }

  return "未命名收藏";
}

interface ItemContextMenuContentProps {
  item: Item;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
  onMove: (item: Item) => void;
  onStartSelection: (item: Item) => void;
  onViewFlowRuns: (item: Item) => void;
}

function ItemContextMenuContent({
  item,
  onEdit,
  onDelete,
  onMove,
  onStartSelection,
  onViewFlowRuns,
}: ItemContextMenuContentProps) {
  const shouldOpenFlowRunsRef = useRef(false);
  const handleOpenWebsite = useCallback(() => {
    window.open(item?.url || "", "_blank");
  }, [item]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(item?.url || "");
    toast.success("链接已复制到剪贴板");
  }, [item]);

  return (
    <ContextMenuContent
      className="w-52"
      onCloseAutoFocus={(event) => {
        if (!shouldOpenFlowRunsRef.current) {
          return;
        }

        shouldOpenFlowRunsRef.current = false;
        event.preventDefault();

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        queueMicrotask(() => onViewFlowRuns(item));
      }}
    >
      <ContextMenuItem onClick={handleOpenWebsite}>
        <ExternalLink /> 访问原网站
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={handleCopyLink}>
        <Link2 /> 复制链接
      </ContextMenuItem>
      <ContextMenuItem disabled>
        <Copy /> 复制内容
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={() => onStartSelection(item)}>
        <CopyCheck /> 批量操作
      </ContextMenuItem>
      <ContextMenuItem onClick={() => onMove(item)}>
        <Folder /> 移动到
      </ContextMenuItem>
      <ContextMenuItem
        onSelect={() => {
          shouldOpenFlowRunsRef.current = true;
        }}
      >
        <Workflow /> 流程记录
      </ContextMenuItem>
      <ContextMenuItem disabled>
        <Tag /> 标签
      </ContextMenuItem>
      <ContextMenuItem onClick={() => onEdit(item)}>
        <FilePen /> 编辑
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem variant="destructive" onClick={() => onDelete(item)}>
        <Trash2 /> 删除
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

interface BatchItemContextMenuContentProps {
  itemId: string;
  selectedCount: number;
  onMove: () => void;
  onAddTags: () => void;
  onRemoveTags: () => void;
  onDelete: () => void;
  onDeselect: (itemId: string) => void;
}

function BatchItemContextMenuContent({
  itemId,
  selectedCount,
  onMove,
  onAddTags,
  onRemoveTags,
  onDelete,
  onDeselect,
}: BatchItemContextMenuContentProps) {
  return (
    <ContextMenuContent className="w-52">
      <ContextMenuItem onClick={onMove}>
        <Folder /> 移动已选 {selectedCount} 项
      </ContextMenuItem>
      <ContextMenuItem onClick={onAddTags}>
        <Tag /> 添加标签
      </ContextMenuItem>
      <ContextMenuItem onClick={onRemoveTags}>
        <Tag /> 删除标签
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={() => onDeselect(itemId)}>
        <SquareMinus /> 取消选择此项
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem variant="destructive" onClick={onDelete}>
        <Trash2 /> 删除已选 {selectedCount} 项
      </ContextMenuItem>
    </ContextMenuContent>
  );
}

export function ItemsDisplay() {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const dialog = useDialog();
  const { open: openSheet } = useSheet();
  const { isMobile } = useSidebar();
  const [editingTextItem, setEditingTextItem] = useState<Item | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [lastSelectedItemId, setLastSelectedItemId] = useState<string | null>(null);
  // 获取当前布局模式（从全局store）
  const mode = useLayoutMode();
  const { closePreview } = useItemActions()
  const openItemPreview = useOpenItemPreview();
  const selectedItemId = useSelectedItemId();
  const routeInfo = useRouteInfo();
  const workspaceContext = useWorkspaceContext();
  const privacyStatus = useFolderPrivacyStatus(workspaceContext.type === "folder" ? workspaceContext.folder_id : undefined);
  const openPrivacyDialog = useFolderPrivacyDialog();
  useEffect(() => {
    if (privacyStatus.isLocked) setEditingTextItem(null);
  }, [privacyStatus.isLocked]);
  // 从 Context 获取数据和加载状态
  const { items, loading, hasMore, error, loadMore, refresh } = useMineItemsData();
  const deleteItemMutation = useDeleteItemMutation();
  const selectedCount = selectedIds.size;
  const allCurrentItemsSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const {
    isSticky: isToolbarSticky,
    isVisible: isToolbarVisible,
    shouldAnimate: shouldAnimateToolbar,
  } = useSmartStickyToolbar(toolbarRef);

  const handleViewFlowRuns = useCallback((item: Item) => {
    void openSheet(ItemFlowRunsSheet, { item }, {
      className: "w-full sm:max-w-xl",
    });
  }, [openSheet]);

  // 设置无限滚动（返回需要绑定的targetRef）
  const { targetRef } = useInfiniteScroll({
    hasMore,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    setSelectedIds((current) => {
      if (current.size === 0) {
        return current;
      }

      const visibleIds = new Set(items.map((item) => item.id));
      const next = new Set(Array.from(current).filter((id) => visibleIds.has(id)));

      return next.size === current.size ? current : next;
    });
  }, [items]);

  useEffect(() => {
    if (selectionMode || selectedIds.size === 0) {
      return;
    }

    setSelectedIds(new Set());
  }, [selectedIds.size, selectionMode]);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setLastSelectedItemId(null);
  }, []);

  const toggleItemSelection = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
    setLastSelectedItemId(id);
  }, []);

  const ensureItemSelected = useCallback((id: string) => {
    setSelectedIds((current) => {
      if (current.has(id)) {
        return current;
      }

      const next = new Set(current);
      next.add(id);
      return next;
    });
    setLastSelectedItemId(id);
  }, []);

  const startSelection = useCallback((item?: Item) => {
    closePreview();
    setSelectionMode(true);
    setSelectedIds(item ? new Set([item.id]) : new Set());
    setLastSelectedItemId(item?.id ?? null);
  }, [closePreview]);

  const toggleSelectAllCurrentItems = useCallback(() => {
    setSelectedIds((current) => {
      if (items.length === 0) {
        return current;
      }

      if (items.every((item) => current.has(item.id))) {
        return new Set();
      }

      return new Set(items.map((item) => item.id));
    });
    setLastSelectedItemId(null);
  }, [items]);

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);

    if (ids.length === 0) {
      return;
    }

    const confirmed = await dialog.confirm({
      title: "批量删除收藏",
      description: `确定要将已选择的 ${ids.length} 个收藏移入回收站吗？`,
      confirmText: "移入回收站",
      cancelText: "取消",
      variant: "destructive",
      closeOnOutside: false,
      closeOnEsc: true,
    });

    if (!confirmed) {
      return;
    }

    deleteItemMutation.mutate(ids, {
      onSuccess: () => {
        if (selectedItemId && ids.includes(selectedItemId)) {
          closePreview();
        }

        toast.success(`已删除 ${ids.length} 个收藏`);
        exitSelectionMode();
      },
    });
  }, [closePreview, deleteItemMutation, dialog, exitSelectionMode, selectedIds, selectedItemId]);

  const handleBulkMove = useCallback(async () => {
    const selectedItems = items.filter((item) => selectedIds.has(item.id));

    if (selectedItems.length === 0) {
      return;
    }

    const result = await dialog.open(MoveItem, { items: selectedItems }, {
      title: "移动收藏",
      description: `将已选择的 ${selectedItems.length} 个收藏移动到指定文件夹。`,
      placement: "top",
      className: "w-full !max-w-lg",
    });

    if (result?.action === "moved") {
      exitSelectionMode();
    }
  }, [dialog, exitSelectionMode, items, selectedIds]);

  const handleBulkAddTags = useCallback(async () => {
    const selectedItems = items.filter((item) => selectedIds.has(item.id));

    if (selectedItems.length === 0) {
      return;
    }

    const result = await dialog.open(BulkAddTags, { items: selectedItems }, {
      title: "添加标签",
      description: `将已选择的 ${selectedItems.length} 个收藏添加到指定标签。`,
      className: "w-full !max-w-lg",
    });

    if (result?.action === "updated") {
      exitSelectionMode();
    }
  }, [dialog, exitSelectionMode, items, selectedIds]);

  const handleBulkRemoveTags = useCallback(async () => {
    const selectedItems = items.filter((item) => selectedIds.has(item.id));

    if (selectedItems.length === 0) {
      return;
    }

    const result = await dialog.open(BulkRemoveTags, { items: selectedItems }, {
      title: "删除标签",
      description: `从已选择的 ${selectedItems.length} 个收藏中删除指定标签。`,
      className: "w-full !max-w-lg",
    });

    if (result?.action === "updated") {
      exitSelectionMode();
    }
  }, [dialog, exitSelectionMode, items, selectedIds]);

  const handleListItemClick = useCallback((item: Item, event?: MouseEvent) => {
    const { id } = item;
    if (selectionMode) {
      if (event?.shiftKey && lastSelectedItemId) {
        event.preventDefault();
        window.getSelection()?.removeAllRanges();

        const currentIndex = items.findIndex((item) => item.id === id);
        const lastIndex = items.findIndex((item) => item.id === lastSelectedItemId);

        if (currentIndex >= 0 && lastIndex >= 0) {
          const start = Math.min(currentIndex, lastIndex);
          const end = Math.max(currentIndex, lastIndex);
          const rangeIds = items.slice(start, end + 1).map((item) => item.id);

          setSelectedIds((current) => {
            const next = new Set(current);
            rangeIds.forEach((rangeId) => next.add(rangeId));
            return next;
          });
          setLastSelectedItemId(id);
          return;
        }
      }

      toggleItemSelection(id);
      return;
    }

    openItemPreview(item, items);
  }, [items, lastSelectedItemId, openItemPreview, selectionMode, toggleItemSelection]);

  const handleListItemDelete = useCallback(async (item: Item, onDeleted?: () => void) => {
    const confirmed = await dialog.confirm({
      title: "删除收藏",
      description: `确定要将「${getActionItemTitle(item)}」移入回收站吗？`,
      confirmText: "移入回收站",
      cancelText: "取消",
      variant: "destructive",
      closeOnOutside: false,
      closeOnEsc: true,
    });

    if (!confirmed) {
      return;
    }

    deleteItemMutation.mutate(item.id, {
      onSuccess: () => {
        toast.success("删除收藏成功");
        onDeleted?.();
      },
    });
  }, [deleteItemMutation, dialog]);

  const handleListItemEdit = useCallback(async (item: Item) => {
    if (item.format === "text") {
      setEditingTextItem(item);
      return;
    }

    if (item.format === "image") {
      const result = await dialog.open(EditImageItem, {
        item,
        onDelete: (targetItem, resolve) => {
          void handleListItemDelete(targetItem, () => {
            resolve({
              action: "deleted",
              itemId: targetItem.id,
            });
          });
        },
      }, {
        title: "编辑图片",
        className: "w-full !max-w-4xl",
      });

      if (result?.action === "deleted") {
        return;
      }

      return;
    }

    const result = await dialog.open(EditItem, {
      item,
      onDelete: (targetItem, resolve) => {
        void handleListItemDelete(targetItem, () => {
          resolve({
            action: "deleted",
            itemId: targetItem.id,
          });
        });
      },
    }, {
      title: "编辑收藏",
      className: 'w-full !max-w-4xl',
    });

    if (result?.action === "deleted") {
      return;
    }
  }, [dialog, handleListItemDelete]);

  const handleListItemMove = useCallback((item: Item) => {
    void dialog.open(MoveItem, { item }, {
      title: "移动项目",
      description: `将 "${getActionItemTitle(item)}" 移动到指定文件夹。`,
      placement: "top",
      className: "w-full !max-w-lg",
    });
  }, [dialog]);

  /**
   * 根据当前布局模式渲染对应的组件
   * 使用useMemo避免mode或items不变时重复渲染
   *
   * 注意：切换mode时，这里会重新计算，但不会重新请求数据
   * 因为useMineItemsData的useEffect依赖中不包含mode
   */
  const renderedItems = useMemo(() => {
    const ItemComponent = ITEM_COMPONENTS[mode];
    const selectedItems = selectionMode
      ? items.filter((item) => selectedIds.has(item.id))
      : [];

    return items.map((item) => {
      const draggedItems = selectionMode && selectedIds.has(item.id)
        ? selectedItems
        : [item];

      return (
      <ContextMenu
        key={item.id}
        modal={false}
        onOpenChange={(open) => {
          if (open && selectionMode) {
            ensureItemSelected(item.id);
          }
        }}
      >
        <ContextMenuTrigger asChild>
          <ItemMoveDraggable
            item={item}
            items={draggedItems}
            titles={draggedItems.map(getActionItemTitle)}
            disabled={isMobile}
            onMoved={selectionMode && selectedIds.has(item.id) ? exitSelectionMode : undefined}
            className={`group/selection-item relative ${mode === "card" ? "h-full" : "rounded-xl"} ${
              selectionMode ? "select-none" : ""
            }`}
          >
            <ItemComponent
              item={item}
              isActive={!selectionMode && selectedItemId === item.id}
              selectionMode={mode === "card" || mode === "list" || mode === "headline" ? selectionMode : undefined}
              isSelected={mode === "card" || mode === "list" || mode === "headline" ? selectedIds.has(item.id) : undefined}
              onStartSelection={!selectionMode && (mode === "list" || mode === "headline" || (mode === "card" && item.format === "image"))
                ? (event) => {
                    event.stopPropagation();
                    startSelection(item);
                  }
                : undefined}
              onEdit={selectionMode ? undefined : () => handleListItemEdit(item)}
              onDelete={selectionMode ? undefined : () => handleListItemDelete(item)}
              onClick={(event) => handleListItemClick(item, event)} />
            {!selectionMode && mode !== "list" && mode !== "headline" && !(mode === "card" && item.format === "image") && (
              <button
                type="button"
                aria-label={`选择${getActionItemTitle(item)}`}
                onClick={(event) => {
                  event.stopPropagation();
                  startSelection(item);
                }}
                className={`pointer-events-none absolute z-40 flex size-5 items-center justify-center rounded-md border-[1.5px] border-border/70 bg-background/90 text-transparent opacity-0 backdrop-blur transition-[opacity,border-color,background-color] duration-150 group-hover/selection-item:pointer-events-auto group-hover/selection-item:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  mode === "card"
                    ? "right-3 bottom-5"
                    : "top-3 left-3"
                }`}
              >
                <Check className="size-3" strokeWidth={2.6} />
              </button>
            )}
            {selectionMode && mode !== "card" && (
              <>
                <div
                  className={`pointer-events-none absolute inset-0 z-30 rounded-xl ring-inset transition-colors duration-200 ${
                    selectedIds.has(item.id)
                      ? "ring-2 ring-primary/70"
                      : "ring-1 ring-transparent"
                  }`}
                />
                {mode !== "list" && mode !== "headline" && (
                  <div
                    className={`pointer-events-none absolute left-3 top-3 z-40 flex size-5 items-center justify-center rounded-md border-[1.5px] backdrop-blur transition-colors duration-200 ${
                      selectedIds.has(item.id)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border/70 bg-background/90 text-transparent"
                    }`}
                  >
                    <Check className="size-3" strokeWidth={2.6} />
                  </div>
                )}
              </>
            )}
          </ItemMoveDraggable>
        </ContextMenuTrigger>
        {selectionMode ? (
          <BatchItemContextMenuContent
            itemId={item.id}
            selectedCount={selectedCount}
            onMove={() => void handleBulkMove()}
            onAddTags={() => void handleBulkAddTags()}
            onRemoveTags={() => void handleBulkRemoveTags()}
            onDelete={() => void handleBulkDelete()}
            onDeselect={toggleItemSelection}
          />
        ) : (
          <ItemContextMenuContent
            item={item}
            onEdit={handleListItemEdit}
            onDelete={handleListItemDelete}
            onMove={handleListItemMove}
            onStartSelection={startSelection}
            onViewFlowRuns={handleViewFlowRuns}
          />
        )}
      </ContextMenu>
      );
    });
  }, [items, mode, selectedCount, selectedIds, selectedItemId, selectionMode, ensureItemSelected, exitSelectionMode, handleBulkAddTags, handleBulkDelete, handleBulkMove, handleBulkRemoveTags, handleListItemClick, handleListItemDelete, handleListItemEdit, handleListItemMove, handleViewFlowRuns, isMobile, startSelection, toggleItemSelection]);

  if (privacyStatus.isLocked) {
    return <div className="flex min-h-[26rem] items-center justify-center px-6 py-10">
      <Empty className="max-w-md gap-5 border-0 bg-transparent px-0 py-0 shadow-none">
        <EmptyHeader className="max-w-md gap-3">
          <EmptyMedia variant="default" className="rounded-full bg-muted p-4"><LockKeyhole className="size-8" /></EmptyMedia>
          <EmptyTitle className="text-xl font-semibold">{privacyStatus.folder?.name ?? "私密文件夹"}已锁定</EmptyTitle>
          <EmptyDescription className="max-w-sm text-sm/6 text-muted-foreground">
            解锁后可查看文件夹及其内容。本标签页的解锁有效期为 15 分钟。
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent><Button onClick={() => void openPrivacyDialog("unlock")}>解锁查看</Button></EmptyContent>
      </Empty>
    </div>;
  }

  // 错误状态展示
  if (error) {
    return (
      <div className="flex min-h-[26rem] items-center justify-center px-6 py-10">
        <Empty className="max-w-md gap-5 border-0 bg-transparent px-0 py-0 shadow-none">
          <EmptyHeader className="max-w-md gap-3">
            <EmptyMedia
              variant="default"
              className="rounded-full bg-destructive/10 p-4 text-destructive"
            >
              <AlertCircle className="h-8 w-8" />
            </EmptyMedia>
            <EmptyTitle className="text-xl font-semibold">{error.title}</EmptyTitle>
            <EmptyDescription className="max-w-sm text-sm/6 text-muted-foreground">
              {error.description || "内容暂时没有加载出来，你可以重新加载后再试一次。"}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent className="gap-4">
            <Button onClick={refresh} variant="outline" size="sm" className="min-w-32">
              <RefreshCw className="mr-2 h-4 w-4" />
              重新加载
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  


  return (
    <div>
      <AddTextOverlay
        item={editingTextItem ?? undefined}
        open={!!editingTextItem}
        onOpenChange={(open) => {
          if (!open) {
            setEditingTextItem(null);
          }
        }}
        routeInfo={routeInfo}
      />
      {/* 筛选器（分类、排序等） */}
      <div
        data-slot="workspace-toolbar"
        data-hidden={!selectionMode && isToolbarSticky && !isToolbarVisible}
        data-animate={!selectionMode && shouldAnimateToolbar}
        data-natural-flow={!selectionMode && !isToolbarSticky}
        ref={toolbarRef}
        className={`z-20 -mx-8 -mt-2 overflow-hidden ${
          selectionMode || isToolbarSticky ? "sticky top-0" : "relative top-0"
        } ${
          selectionMode || !isToolbarSticky || isToolbarVisible
            ? ""
            : "pointer-events-none"
        }`}
      >
        <div
          data-slot="workspace-toolbar-surface"
          className="px-8 pt-2"
        >
          {selectionMode ? (
            <BulkSelectionToolbar
              selectedCount={selectedCount}
              allSelected={allCurrentItemsSelected}
              disabled={items.length === 0}
              onToggleAll={toggleSelectAllCurrentItems}
              onClose={exitSelectionMode}
            >
              <button
                type="button"
                onClick={() => void handleBulkMove()}
                disabled={selectedCount === 0}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-45"
              >
                <Folder className="size-3.5" />
                移动
              </button>

              <button
                type="button"
                onClick={() => void handleBulkAddTags()}
                disabled={selectedCount === 0}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-45"
              >
                <Tag className="size-3.5" />
                添加标签
              </button>

              <button
                type="button"
                onClick={() => void handleBulkRemoveTags()}
                disabled={selectedCount === 0}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-45"
              >
                <Tag className="size-3.5" />
                删除标签
              </button>

              <button
                type="button"
                onClick={() => void handleBulkDelete()}
                disabled={selectedCount === 0 || deleteItemMutation.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:pointer-events-none disabled:text-muted-foreground"
              >
                {deleteItemMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )}
                删除
              </button>
            </BulkSelectionToolbar>
          ) : (
            <ItemsFilter />
          )}
        </div>
      </div>

      {/* 数据展示区 */}
      {loading && items.length === 0 ? (
        // 首次加载：显示骨架屏
        <ItemSkeleton mode={mode} count={12} />
      ) : items.length > 0 ? (
        // 有数据：正常展示
        <>
          {/* 根据布局模式应用不同的容器样式 */}
          <div data-layout-mode={mode} className={`${getContainerClass(mode)} py-4`}>
            {renderedItems}
          </div>

          {/* 无限滚动触发器 */}
          {hasMore && (
            <div
              ref={targetRef} // 绑定ref，当这个元素进入可视区域时触发加载
              className="flex items-center justify-center py-8"
            >
              {loading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">加载中...</span>
                </div>
              )}
            </div>
          )}

          {/* 没有更多数据提示 */}
          {!hasMore && (
            <div className="text-center py-8 text-muted-foreground text-xs">
              没有更多内容了
            </div>
          )}
        </>
      ) : (
        // 加载完成且无数据：显示空状态
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {workspaceContext.duplicatesOnly ? <CopyMinus /> : <AppWindow />}
            </EmptyMedia>
            <EmptyTitle>{workspaceContext.duplicatesOnly ? "没有发现重复收藏" : workspaceContext.keyword ? "没有找到相关收藏" : "暂无数据"}</EmptyTitle>
            <EmptyDescription>
              {workspaceContext.duplicatesOnly
                ? "当前可查看的收藏中暂无重复候选"
                : workspaceContext.keyword
                ? `没有搜索到与 “${workspaceContext.keyword}” 相关的收藏`
                : "暂无数据，请先添加网站"}
            </EmptyDescription>
          </EmptyHeader>
          {!workspaceContext.keyword && !workspaceContext.duplicatesOnly && <EmptyContent>
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  void dialog.open(AddLink, { routeInfo }, {
                    title: "添加网页",
                    description: "粘贴网址，我们将自动解析内容。",
                  })
                }
                size="sm"
              >
                添加网站
              </Button>
            </div>
          </EmptyContent>}
        </Empty>
      )}
    </div>
  );
}

export default ItemsDisplay;
